import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { SignedIn, SignedOut, RedirectToSignIn, useUser } from '@clerk/clerk-react';
import theme from './theme';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import CompleteProfilePage from './pages/CompleteProfilePage';
import MainLayout from './components/MainLayout';
import ContentPage from './pages/ContentPage';
import PracticePage from './pages/PracticePage';
import DashboardPage from './pages/DashboardPage';
import SettingsPage from './pages/SettingsPage';

// Component that checks if user profile is complete
const RequireCompleteProfile = ({ children }) => {
  const { isLoaded, user } = useUser();

  if (!isLoaded) {
    return null; // Or a loading spinner
  }

  const isProfileComplete = user?.publicMetadata?.isProfileComplete;

  if (!isProfileComplete) {
    return <Navigate to="/complete-profile" replace />;
  }

  return children;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Routes>
        {/* Public auth routes */}
        <Route path="/sign-in/*" element={
          <SignedOut>
            <SignInPage />
          </SignedOut>
        } />
        <Route path="/sign-up/*" element={
          <SignedOut>
            <SignUpPage />
          </SignedOut>
        } />

        {/* Profile completion - requires auth but not complete profile */}
        <Route path="/complete-profile" element={
          <>
            <SignedIn>
              <CompleteProfilePage />
            </SignedIn>
            <SignedOut>
              <RedirectToSignIn />
            </SignedOut>
          </>
        } />

        {/* Protected routes - require auth AND complete profile */}
        <Route path="/dashboard" element={
          <>
            <SignedIn>
              <RequireCompleteProfile>
                <MainLayout>
                  <DashboardPage />
                </MainLayout>
              </RequireCompleteProfile>
            </SignedIn>
            <SignedOut>
              <RedirectToSignIn />
            </SignedOut>
          </>
        } />
        <Route path="/content" element={
          <>
            <SignedIn>
              <RequireCompleteProfile>
                <MainLayout>
                  <ContentPage />
                </MainLayout>
              </RequireCompleteProfile>
            </SignedIn>
            <SignedOut>
              <RedirectToSignIn />
            </SignedOut>
          </>
        } />
        <Route path="/practice" element={
          <>
            <SignedIn>
              <RequireCompleteProfile>
                <MainLayout>
                  <PracticePage />
                </MainLayout>
              </RequireCompleteProfile>
            </SignedIn>
            <SignedOut>
              <RedirectToSignIn />
            </SignedOut>
          </>
        } />
        <Route path="/settings" element={
          <>
            <SignedIn>
              <RequireCompleteProfile>
                <MainLayout>
                  <SettingsPage />
                </MainLayout>
              </RequireCompleteProfile>
            </SignedIn>
            <SignedOut>
              <RedirectToSignIn />
            </SignedOut>
          </>
        } />

        {/* Redirect root based on auth status */}
        <Route path="/" element={
          <>
            <SignedIn>
              <Navigate to="/dashboard" replace />
            </SignedIn>
            <SignedOut>
              <Navigate to="/sign-in" replace />
            </SignedOut>
          </>
        } />

        {/* Legacy login route redirect */}
        <Route path="/login" element={<Navigate to="/sign-in" replace />} />
      </Routes>
    </ThemeProvider>
  );
}

export default App;
